import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { MapPin, Phone, Clock, CreditCard, Shield } from 'lucide-react';
import { Card, CardContent } from './ui/card';

const LocationSection = () => {
  const { t } = useLanguage();

  const infoItems = [
    {
      icon: Shield,
      title: t('location.insurance'),
    },
    {
      icon: CreditCard,
      title: t('location.payment'),
    },
  ];

  const businessHours = [
    { day: t('location.monday'), hours: '9 AM – 5 PM' },
    { day: t('location.tuesday'), hours: '9 AM – 5 PM' },
    { day: t('location.wednesday'), hours: '9 AM – 5 PM' },
    { day: t('location.thursday'), hours: '9 AM – 5 PM' },
    { day: t('location.friday'), hours: '9 AM – 5 PM' },
    { day: t('location.saturday'), hours: t('location.closed') },
    { day: t('location.sunday'), hours: t('location.closed') },
  ];

  return (
    <section 
      id="location"
      className="section-padding bg-cream relative overflow-hidden"
      aria-labelledby="location-title"
    >
      <div className="container-main">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-12">
          <span 
            className="inline-block font-body text-sm uppercase tracking-widest font-semibold text-sage mb-4"
            data-testid="location-caption"
          >
            {t('location.caption')}
          </span>
          <h2 
            id="location-title"
            className="font-heading text-4xl md:text-5xl font-semibold text-[#1C1C1E] tracking-tight"
            data-testid="location-title"
          >
            {t('location.title')}
          </h2>
        </div>

        <div className="grid lg:grid-cols-2 gap-8 items-stretch">
          {/* Info - Now on Left */}
          <div className="space-y-6 flex flex-col">
            {/* Address Card */}
            <Card className="service-card border-earth-dark" data-testid="location-address-card">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-sage rounded-2xl flex items-center justify-center shadow-lg">
                    <MapPin className="w-6 h-6 text-white" aria-hidden="true" />
                  </div>
                  <div>
                    <h3 className="font-heading text-lg font-semibold text-[#1C1C1E] mb-2">
                      Healing Clinic / 힐링한의원
                    </h3>
                    <address className="font-body text-muted-foreground not-italic leading-relaxed text-sm">
                      <span className="block">{t('location.address')}</span>
                      <span className="block">{t('location.city')}</span>
                      <span className="block text-sage font-medium mt-1">
                        ({t('location.plaza')})
                      </span>
                    </address>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Phone Card */}
            <Card className="service-card border-earth-dark" data-testid="location-phone-card">
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-gold rounded-2xl flex items-center justify-center shadow-lg">
                    <Phone className="w-6 h-6 text-white" aria-hidden="true" />
                  </div>
                  <div>
                    <h3 className="font-heading text-lg font-semibold text-[#1C1C1E] mb-1">
                      {t('nav.bookNow')}
                    </h3>
                    <a 
                      href="tel:+17145915360"
                      className="font-body text-xl font-bold text-sage hover:text-sage-dark transition-colors duration-200"
                      data-testid="location-phone-link"
                    >
                      (714) 591-5360
                    </a>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Info Items - 2 boxes only */}
            <div className="grid grid-cols-2 gap-4">
              {infoItems.map((item, index) => (
                <div 
                  key={index}
                  className="flex items-center gap-3 p-4 rounded-xl bg-white/60"
                  data-testid={`location-info-${index}`}
                >
                  <item.icon 
                    className="w-5 h-5 flex-shrink-0 text-muted-foreground" 
                    aria-hidden="true" 
                  />
                  <span className="font-body text-sm font-medium text-[#1C1C1E]">
                    {item.title}
                  </span>
                </div>
              ))}
            </div>

            {/* Business Hours - Single column */}
            <Card className="service-card border-earth-dark flex-1" data-testid="location-hours-card">
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <Clock className="w-5 h-5 text-sage" aria-hidden="true" />
                  <h3 className="font-heading text-lg font-semibold text-[#1C1C1E]">
                    {t('location.hours')}
                  </h3>
                </div>
                <div className="space-y-0">
                  {businessHours.map((item, index) => (
                    <div 
                      key={index}
                      className="flex justify-between items-center py-2 border-b border-earth-dark/30 last:border-0"
                      data-testid={`hours-${index}`}
                    >
                      <span className="font-body text-sm text-[#1C1C1E]">{item.day}</span>
                      <span className={`font-body text-sm ${item.hours === t('location.closed') ? 'text-muted-foreground' : 'text-sage font-medium'}`}>
                        {item.hours}
                      </span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Map - Now on Right */}
          <div className="map-container h-full" data-testid="google-map">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3316.8837025961!2d-117.96251492357!3d33.77385807325!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x80dd2952821baadb%3A0x55cbda3759e16a31!2sHealing%20Clinic%20%2F%20%ED%9E%90%EB%A7%81%ED%95%9C%EC%9D%98%EC%9B%90!5e0!3m2!1sen!2sus!4v1700000000000!5m2!1sen!2sus"
              width="100%"
              height="100%"
              style={{ border: 0, minHeight: '500px' }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Healing Clinic Location - 9860 Garden Grove Blvd, Garden Grove, CA 92844"
              aria-label="Google Maps showing Healing Clinic location"
            ></iframe>
          </div>
        </div>
      </div>
    </section>
  );
};

export default LocationSection;
